import styles from './LegacyPersonalProfileBanner.module.scss';

import React, { useEffect } from 'react';
import { M } from '@dashboard-experience/mastodon';
import T from 'components/T';
import { i18n } from '@international/mastodon-i18n';
import { connect } from 'react-redux';

import { ReportSummary } from 'api/candidates/types/Report';
import { CHECKR_PERSONAL_ANALYTICS_EVENTS } from 'lib/analytics';
import { trackAnalyticsEvent } from 'actions/analytics';
import { PERSONAL_URL } from '../../constants';

import { useCheckrPersonalBannerState } from './useCheckrPersonalBannerState';

// Constants
const GENERIC_COPY_VERSION = '11-05-2025';

// Types
// Where the banner is rendered, sent as the `location` analytics property on
// the viewed event so we can tell the multi-report landing page apart from an
// individual report page.
type BannerLocation = 'home' | 'report';

type BannerProps = {
  onDismiss: () => void;
  trackEvent: (eventName: string, eventParams?: Record<string, any>) => void;
  applicantId: string;
  reports: ReportSummary[];
  reportId?: string;
  reportStatus?: string;
  token?: string;
  location: BannerLocation;
  candidateId?: string;
};

// Helper functions
const generatePersonalChecksUrl = (
  candidateId?: string,
  token?: string,
): string => {
  const url = new URL('/api/auth/candidate', PERSONAL_URL);
  url.searchParams.set('utm_source', 'candidate_portal');
  url.searchParams.set('utm_medium', 'personal_checks_banner');
  url.searchParams.set('utm_campaign', 'personal_checks_banner');

  if (candidateId) {
    url.searchParams.set('candidate_id', candidateId);
  }

  if (token) {
    url.searchParams.set('token', token);
  }

  return url.toString();
};

// Custom hooks
const BANNER_VIEWED_SESSION_KEY = 'personalChecksBannerViewedThisSession';

const useBannerAnalytics = ({
  trackEvent,
  applicantId,
  reports,
  reportId,
  reportStatus,
  copyVersion,
  location,
  variant,
}: {
  trackEvent: (eventName: string, eventParams?: Record<string, any>) => void;
  applicantId: string;
  reports: ReportSummary[];
  reportId: string | undefined;
  reportStatus: string | undefined;
  copyVersion: string;
  location: BannerLocation;
  variant: string;
}) => {
  useEffect(() => {
    if (sessionStorage.getItem(BANNER_VIEWED_SESSION_KEY)) {
      return;
    }
    sessionStorage.setItem(BANNER_VIEWED_SESSION_KEY, 'true');

    trackEvent(CHECKR_PERSONAL_ANALYTICS_EVENTS.LEGACY_BANNER_VIEWED, {
      applicantId,
      reportIds: reports.map(report => report.id),
      reportId,
      reportStatus,
      copyVersion,
      location,
      variant,
    });
  }, [
    applicantId,
    reports,
    reportId,
    reportStatus,
    trackEvent,
    copyVersion,
    location,
    variant,
  ]);

  const trackDismissClick = () => {
    trackEvent(CHECKR_PERSONAL_ANALYTICS_EVENTS.LEGACY_BANNER_DISMISSED, {
      applicantId,
      copyVersion,
      location,
      variant,
    });
  };

  const trackCtaClick = () => {
    trackEvent(CHECKR_PERSONAL_ANALYTICS_EVENTS.LEGACY_BANNER_CTA_CLICKED, {
      applicantId,
      copyVersion,
      location,
      variant,
    });
  };

  return { trackDismissClick, trackCtaClick };
};

// Shared components
const DismissButtonImpl = ({ onClick }: { onClick: () => void }) => {
  return (
    <button
      className={styles.dismissButton}
      onClick={onClick}
      type='button'
      aria-label={i18n.getStr('components.PersonalChecks.banner.dismiss')}
    >
      <M.Icon icon='Close' style={{ fill: 'rgba(107, 114, 128)' }} size={24} />
    </button>
  );
};
const DismissButton = i18n.renderTranslation()(DismissButtonImpl);

// Container to control the display logic of the banner
function LegacyPersonalProfileBanner({
  reportId,
  reportStatus,
  reports,
  applicantId,
  token,
  trackEvent,
  location = 'report',
}: Readonly<{
  reportId?: string;
  reportStatus?: string;
  reports: ReportSummary[];
  applicantId: string;
  token?: string;
  trackEvent: (eventName: string, eventParams?: Record<string, any>) => void;
  location?: BannerLocation;
}>) {
  const { isVisible, dismiss, candidateId } = useCheckrPersonalBannerState();

  if (!isVisible) {
    return null;
  }

  return (
    <SetupProfileBanner
      onDismiss={dismiss}
      trackEvent={trackEvent}
      applicantId={applicantId}
      reports={reports}
      reportId={reportId}
      reportStatus={reportStatus}
      token={token}
      location={location}
      candidateId={candidateId}
    />
  );
}

const mapStateToProps = (state: any) => {
  const { reports, applicantId, token } = state.portal;

  return { reports, applicantId, token };
};

const mapDispatchToProps = (dispatch: any) => {
  return {
    trackEvent: (eventName: string, eventParams?: Record<string, any>) =>
      dispatch(trackAnalyticsEvent(eventName, eventParams)),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(LegacyPersonalProfileBanner);

function SetupProfileBanner({
  onDismiss,
  trackEvent,
  applicantId,
  reports,
  reportId,
  reportStatus,
  token,
  location,
  candidateId,
}: BannerProps) {
  const { trackDismissClick, trackCtaClick } = useBannerAnalytics({
    trackEvent,
    applicantId,
    reports,
    reportId,
    reportStatus,
    copyVersion: GENERIC_COPY_VERSION,
    location,
    variant: 'setupProfile',
  });

  const handleDismiss = () => {
    trackDismissClick();
    onDismiss();
  };

  return (
    <div className={styles.personalChecksBanner}>
      <DismissButton onClick={handleDismiss} />
      <div className={styles.bannerVariantContent}>
        <h4 className={styles.header}>
          <T value='components.PersonalChecks.setupProfileBanner.title' />
        </h4>
        <a
          href={generatePersonalChecksUrl(candidateId, token)}
          target='_blank'
          rel='noopener noreferrer'
          className={styles.claimButtonVariant}
          onClick={() => {
            trackCtaClick();
          }}
        >
          <T value='components.PersonalChecks.setupProfileBanner.button' />
        </a>
      </div>
    </div>
  );
}
